/**
 * Shared utility functions for the Lego design system.
 * Importable by any package that depends on @lucid/lego.
 */

/*
|--------------------------------------------------------------------------
| SECURITY HELPERS
|--------------------------------------------------------------------------
*/

export const escapeHtml = (str: string): string =>
  str
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');

/*
|--------------------------------------------------------------------------
| CURRENCY HELPERS
|--------------------------------------------------------------------------
*/

/**
 * Get the currency code for a given locale
 * @param locale - The locale string (e.g., 'en-us', 'de-de')
 * @returns ISO 4217 currency code
 */
export const getCurrencyCodeForLocale = (locale: string): string => {
  if (!locale) return 'EUR';
  const normalizedLocale = locale.toLowerCase();
  switch (normalizedLocale) {
    case 'en':
    case 'en-us':
      return 'USD';
    case 'en-ca':
    case 'fr-ca':
      return 'CAD';
    case 'de-de':
    case 'nl-nl':
    case 'en-be':
    case 'fr-be':
    case 'nl-be':
    case 'de-be':
      return 'EUR';
    case 'nb-no':
      return 'NOK';
    case 'da-dk':
      return 'DKK';
    case 'ar-sa':
    case 'en-sa':
      return 'SAR';
    case 'en-ae':
    case 'ar-ae':
      return 'AED';
    case 'fr-ch':
    case 'de-ch':
      return 'CHF';
    default:
      return 'USD';
  }
};

/**
 * Format a numeric amount as a currency string using a currency code.
 * This is a legacy wrapper around formatPrice for backward compatibility.
 * Returns '—' when the amount is undefined (for display in tables/lists).
 * @deprecated Use `formatPrice` instead with locale and currency options
 * @param amount - Numeric amount to format, or undefined
 * @param currencyCode - ISO 4217 currency code (default: 'USD')
 * @returns Formatted currency string or '—' when undefined
 * @example
 * ```ts
 * formatCurrencyByCode(1234.56)         // "$1,234.56"
 * formatCurrencyByCode(1234.56, 'EUR')  // "€1,234.56"
 * formatCurrencyByCode(undefined)       // "—"
 * ```
 */
export const formatCurrencyByCode = (amount: number | undefined, currencyCode = 'USD'): string => {
  if (amount === undefined) return '—';
  return formatPrice(amount, { currency: currencyCode, locale: 'en-US' });
};
export type FormatPriceOptions = {
  locale?: string;
  currency?: string;
  fallback?: string;
  minimumFractionDigits?: number;
  maximumFractionDigits?: number;
};

/**
 * Format a numeric amount as a locale-aware currency string.
 * @param value - Numeric amount to format
 * @param options - Formatting options { locale, currency, fallback, min/maximumFractionDigits }
 * @returns Formatted currency string (e.g., "$1,234.56") or fallback ('—')
 * @example
 * ```ts
 * formatPrice(1234.56)                                    // "$1,234.56"
 * formatPrice(1234.56, { locale: 'de-DE', currency: 'EUR' }) // "1.234,56 €"
 * formatPrice(1234.56, { locale: 'en-BE', currency: 'EUR' }) // "€1,234.56"
 * formatPrice(undefined, { fallback: '-' })               // "-"
 * ```
 */
export const formatPrice = (
  value: string | number | null | undefined,
  options?: FormatPriceOptions
): string => {
  const {
    locale = 'en-US',
    currency,
    fallback = '—',
    minimumFractionDigits,
    maximumFractionDigits,
  } = options || {};

  const resolvedLocale = locale;
  const resolvedCurrency = currency || getCurrencyCodeForLocale(resolvedLocale);

  // Return fallback for empty/invalid values
  if (value === null || value === undefined || value === '') return fallback;

  // Convert string to number if needed
  let numValue: number;
  if (typeof value === 'string') {
    const normalized = value.trim();
    const isNegative = normalized.startsWith('-');

    // Remove non-numeric characters except dots and commas
    const cleaned = normalized.replace(/[^\d.,]/g, '');
    // Try parsing as number (handles both "1234.56" and "1.234,56")
    numValue = parseFloat(cleaned.replace(/[,.]/g, (match) => (match === '.' ? '.' : '')));
    if (isNaN(numValue)) return fallback;
    if (isNegative) numValue *= -1;
  } else {
    numValue = value;
  }

  try {
    const formatOptions: Intl.NumberFormatOptions = {
      style: 'currency',
      currency: resolvedCurrency,
    };
    if (minimumFractionDigits !== undefined)
      formatOptions.minimumFractionDigits = minimumFractionDigits;
    if (maximumFractionDigits !== undefined)
      formatOptions.maximumFractionDigits = maximumFractionDigits;
    return new Intl.NumberFormat(resolvedLocale, formatOptions).format(numValue);
  } catch {
    return fallback;
  }
};
